import type { IoTDataPoint } from '@/features/realtime/types/realtime.types';
import { formatMeasureNumber, formatMeasureWithUnit } from '@/utils/format';

export type StationChannelReadingRow = {
  channelId: string;
  routingKey: string;
  value: number;
  unit: string;
  label: string;
  breached?: boolean;
};

export const RAINFALL_ROUTING_KEY = 'rainfall';

const UNIT_BY_ROUTING: Record<string, string> = {
  water_level: 'm',
  rainfall: 'mm',
  soil_moisture: '%',
  temperature: '°C',
  humidity: '%',
};

export function normalizeRoutingKey(rk: string | undefined | null): string {
  return rk?.trim().toLowerCase() ?? '';
}

/** Trạm có kênh lượng mưa (theo routing key từ API, không theo `type` bị override). */
export function stationHasRainfallChannel(station: IoTDataPoint): boolean {
  if (normalizeRoutingKey(station.sensorChannelCode) === RAINFALL_ROUTING_KEY) return true;
  if (station.readingsByRoutingKey?.[RAINFALL_ROUTING_KEY] != null) return true;
  for (const rk of Object.values(station.channelCodeById ?? {})) {
    if (normalizeRoutingKey(rk) === RAINFALL_ROUTING_KEY) return true;
  }
  return false;
}

export function unitForRoutingKey(rk: string, fallback = ''): string {
  return UNIT_BY_ROUTING[rk.trim().toLowerCase()] ?? fallback;
}

/** Danh sách số đo theo kênh trên trạm (cho popup/modal/tooltip). */
export function formatStationChannelReadings(
  station: IoTDataPoint,
  labelForRouting?: (rk: string) => string,
): StationChannelReadingRow[] {
  const byRk = station.readingsByRoutingKey ?? {};
  const byId = station.readingsByChannelId ?? {};
  const rows: StationChannelReadingRow[] = [];
  const seen = new Set<string>();

  for (const [rk, value] of Object.entries(byRk)) {
    if (value == null || Number.isNaN(value)) continue;
    seen.add(rk);
    rows.push({
      channelId: '',
      routingKey: rk,
      value,
      unit: unitForRoutingKey(rk, station.unit),
      label: labelForRouting?.(rk) ?? rk,
    });
  }

  for (const [channelId, value] of Object.entries(byId)) {
    if (value == null || Number.isNaN(value)) continue;
    const rk =
      station.channelCodeById?.[channelId] ||
      (station.sensorChannelId === channelId ? station.sensorChannelCode : undefined) ||
      channelId;
    if (seen.has(rk)) continue;
    rows.push({
      channelId,
      routingKey: rk,
      value,
      unit: unitForRoutingKey(rk, station.unit),
      label: labelForRouting?.(rk) ?? rk,
    });
  }

  if (rows.length === 0 && Number.isFinite(station.value)) {
    const rk = station.sensorChannelCode ?? station.type ?? 'sensor';
    rows.push({
      channelId: station.sensorChannelId ?? '',
      routingKey: rk,
      value: station.value,
      unit: station.unit || unitForRoutingKey(rk),
      label: labelForRouting?.(rk) ?? station.sensorTypeName ?? rk,
    });
  }

  return rows;
}

/** Giá trị số đo đã làm tròn hiển thị (2 chữ số thập phân). */
export function formatReadingDisplayValue(value: number): string {
  return formatMeasureNumber(value);
}

export function formatReadingDisplay(value: number, unit?: string): string {
  return formatMeasureWithUnit(value, unit);
}

/** Một dòng tóm tắt giá trị (đa kênh hoặc primary). */
export function formatStationValueSummary(station: IoTDataPoint): string {
  const rows = formatStationChannelReadings(station);
  if (rows.length > 1) {
    return rows.map((r) => formatReadingDisplay(r.value, r.unit)).join(' · ');
  }
  const u = station.unit?.trim() || rows[0]?.unit || '';
  return Number.isFinite(station.value) ? formatReadingDisplay(station.value, u) : '—';
}

export function maxRainfallFromStation(station: IoTDataPoint): number | undefined {
  const v = station.readingsByRoutingKey?.[RAINFALL_ROUTING_KEY];
  if (v != null && Number.isFinite(v)) return v;
  if (normalizeRoutingKey(station.sensorChannelCode) === RAINFALL_ROUTING_KEY) {
    return Number.isFinite(station.value) ? station.value : undefined;
  }
  return undefined;
}
